import { forwardRef, JSX, useImperativeHandle, useRef, useState } from 'react';
import Autocomplete, { type IAutocomplete, type IAutocompleteHistoryProps } from '../AutocompleteBase';
import type { IMunicipality } from '../../../models/municipality.model';
import type { IAutocompleteItem } from '../../../models/autocomplete-item.model';
import MunicipalityApiModel from './models/municipality.api-model';
import ValidationInfo from '../../../validation/validation-info';
import ValidationEngine from '../../../validation/validation-engine';
import type { IServerValidationError } from '../validatable.interface';
import RequiredAsterisk from '../RequiredAsterisk';
import { TranslationService } from '../../../services/translation.service';
import { AutocompleteLabelPosition, AutocompleteSuggestionSource } from '../AutocompleteBase/autocomplete-base.enum';

export interface IMunicipalityAutocompleteState {
  value: IMunicipality | undefined;
  validationInfo: ValidationInfo;
}

export interface IMunicipalityAutocompleteProps {
  id: string;
  serverValidationName?: string;
  labelText?: string;
  labelPosition?: AutocompleteLabelPosition;
  placeholderText?: string;
  required?: boolean;
  className?: string;
  noResultsText?: string;
  disabled?: boolean;
  historyProps?: IAutocompleteHistoryProps;
  state: IMunicipalityAutocompleteState;
  onSelect?: (item: IMunicipality | undefined) => void;
  onKeyUp?: (() => void) | undefined;
  onFocus?: (() => void) | undefined;
}

function createResourceUrl(searchText: string): string {
  return `/api/municipality-autocomplete?q=${searchText}`;
}

function processReceivedData(data: unknown | undefined): IAutocompleteItem[] | undefined {
  return (data as MunicipalityApiModel[])?.map(MunicipalityApiModel.mapToBusinessModel) ?? [];
}

function getSuggestionId(componentId: string, item: IAutocompleteItem): string | undefined {
  if (!item) return undefined;
  const municipality = item as IMunicipality;
  return `autocomplete-result-item-${componentId}-${municipality.gid}`;
}

function getItemId(item: IAutocompleteItem): string | undefined {
  return (item as IMunicipality)?.gid;
}

function getDisplayName(item: IAutocompleteItem | undefined): string | undefined {
  return (item as IMunicipality)?.name;
}

function createSuggestionElement(suggestionId: string, item: IAutocompleteItem, isActive: boolean, suggestionSource: AutocompleteSuggestionSource, onClick: () => void): JSX.Element {
  return (
    <li
      id={suggestionId}
      key={suggestionId}
      role="option"
      aria-selected={isActive}
      className={`autocomplete-result d-flex align-items-center py-2 px-2 m-0 w-100 border-x border-top ${isActive ? 'is-active' : ''}`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick();
        }
      }}
    >
      {(suggestionSource === AutocompleteSuggestionSource.history || suggestionSource === AutocompleteSuggestionSource.commonsearches) && (
        <span className="visually-hidden">
          {TranslationService.get(suggestionSource === AutocompleteSuggestionSource.history ? 'common.autocomplete.recentsearch' : 'common.autocomplete.commonsearch')}
        </span>
      )}
      <span className="d-block text-truncate">{getDisplayName(item)}</span>
    </li>
  );
}

export default forwardRef<IAutocomplete, IMunicipalityAutocompleteProps>(function MunicipalityAutocomplete(
  {
    id,
    serverValidationName = undefined,
    labelText,
    labelPosition = AutocompleteLabelPosition.above,
    placeholderText,
    required = false,
    noResultsText,
    className = undefined,
    disabled = false,
    historyProps = undefined,
    state,
    onSelect = () => {},
    onKeyUp,
    onFocus,
  }: IMunicipalityAutocompleteProps,
  ref
) {
  // === STATE ===
  const [searchText, setSearchText] = useState<string>('');

  // === REFS ===
  const autocompleteRef = useRef<IAutocomplete>(null);

  useImperativeHandle(ref, () => ({
    clear() {
      setSearchText('');
      autocompleteRef.current?.clear();
    },
    addSelectedItemToHistory(item: IAutocompleteItem) {
      autocompleteRef.current?.addSelectedItemToHistory(item);
    },
    checkValidity(): ValidationInfo {
      const updatedValidationInfo = new ValidationInfo(`${labelText} - `);
      ValidationEngine.for(updatedValidationInfo)
        .requiredIf(required, state.value !== undefined, 'common.validation.fromto.stoprequired')
        .validate();
      return updatedValidationInfo;
    },
    handleServerValidationErrors(serverValidationErrors: IServerValidationError[]): ValidationInfo {
      return new ValidationInfo(
        `${labelText} - `,
        serverValidationErrors
          .filter((e) => e.name === `${serverValidationName}Gid` || e.name === `${serverValidationName}Name`)
          .map((e) => e.errorMessages)
          .reduce((a, b) => a.concat(b), []),
        {}
      );
    },
  }));

  return (
    <div>
      {labelPosition === AutocompleteLabelPosition.above && labelText && (
        <label id={`${id}-label`} htmlFor={id}>
          {labelText}
          {required && <RequiredAsterisk />}
        </label>
      )}
      <Autocomplete
        selectedItem={state.value}
        searchText={searchText}
        id={id}
        ref={autocompleteRef}
        label={labelPosition === AutocompleteLabelPosition.inside ? labelText : undefined}
        placeholderText={placeholderText}
        required={required}
        noResultsText={noResultsText}
        className={className}
        disabled={disabled}
        historyProps={
          historyProps
            ? {
                ...historyProps,
                mapHistoryStringToItems: (s) => JSON.parse(s) as IMunicipality[],
                mapItemsToHistoryString: (x) => JSON.stringify(x),
              }
            : undefined
        }
        isInvalid={state.validationInfo.isInvalid}
        ariaDescribedBy={state.validationInfo.isInvalid ? `${id}-error` : undefined}
        listAriaLabelledBy={labelPosition === AutocompleteLabelPosition.above ? `${id}-label` : undefined}
        createResourceUrl={createResourceUrl}
        processReceivedData={processReceivedData}
        getSuggestionId={getSuggestionId}
        getItemId={getItemId}
        getDisplayName={getDisplayName}
        createSuggestionElement={createSuggestionElement}
        onSelect={(item: IAutocompleteItem | undefined) => {
          if (item !== undefined) {
            setSearchText('');
          }
          onSelect?.(item as IMunicipality | undefined);
          onKeyUp?.();
        }}
        onSearchTextChange={(newSearchText: string) => {
          setSearchText(newSearchText);
          onKeyUp?.();
        }}
        onFocus={onFocus}
      />
      <div id={`${id}-error`} className={`invalid-feedback ${state.validationInfo.isInvalid ? 'd-block' : ''}`} aria-live="polite">
        {state.validationInfo.errors.map((error) => (
          <div key={error}>{error}</div>
        ))}
      </div>
    </div>
  );
});
